import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnDestroy, OnInit, Output, Renderer2, RendererStyleFlags2, ViewChild } from '@angular/core';
import { fromEvent } from 'rxjs';
import { pairwise, switchMap, takeUntil } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

import { CarouselData } from '@core/models/carousel-data';

@Component({
	selector: 'app-photo-viewer',
	templateUrl: './photo-viewer.component.html',
	styleUrls: ['./photo-viewer.component.scss']
})
export class PhotoViewerComponent implements OnInit, AfterViewInit, OnDestroy {

	@Input() carouselData: CarouselData;
	@Output() closeOverlay = new EventEmitter<any>();

	// Display/drawing canvas
	@ViewChild('canvas') public canvas: ElementRef;
	private canvasEl: HTMLCanvasElement;
	private cx: CanvasRenderingContext2D;

	// Original canvas
	@ViewChild('originalCanvas') public originalCanvas: ElementRef;
	private originalCanvasEl: HTMLCanvasElement;
	private originalCx: CanvasRenderingContext2D;
	private originalImg: HTMLImageElement;

	private img: HTMLImageElement;
	private imageWidth: number;
	private imageHeight: number;
	private strokeColor: string;
	private lineWidth: number = 5;

	public imgOptions = false;
	public showSavingLoader = false;

	@HostListener('window:orientationchange', ['$event'])
	onOrientationChange(event) {

		var dataUrl = this.canvasEl.toDataURL();

		this.img = new Image();
		this.img.src = dataUrl;

		let that = this;

		this.img.onload = function () {

			setTimeout(() => {

				that.resizeCanvas();
			}, 200);
		};
	}

	constructor(
		private renderer: Renderer2,
		private cdRef: ChangeDetectorRef,
		@Inject(DOCUMENT) private document: Document,
		private window: Window) { }

	ngOnInit(): void {

		// Add display none to the app-root for height issues with mobile address bar
		this.renderer.addClass(this.document.getElementById('app-root'), 'd-none');
		// Add class to body to stop refresh browser action on mobile when swiping down
		this.renderer.addClass(this.document.body, 'no-refresh');
	}

	public ngAfterViewInit() {

		// Setup display/drawing canvas
		this.canvasEl = this.canvas.nativeElement;
		this.cx = this.canvasEl.getContext('2d');
		this.cx.strokeStyle = '#000';

		this.img = new Image();
		this.originalImg = new Image();

		const src = this.carouselData.src;
		if (src.changingThisBreaksApplicationSecurity) {
			this.img.src = src.changingThisBreaksApplicationSecurity;
			this.originalImg.src = src.changingThisBreaksApplicationSecurity;
		} else {
			this.img.src = src;
			this.originalImg.src = src;
		}

		this.imageWidth = this.img.width;
		this.imageHeight = this.img.height;

		// Setup original canvas
		this.originalCanvasEl = this.originalCanvas.nativeElement;
		this.originalCanvasEl.width = this.imageWidth;
		this.originalCanvasEl.height = this.imageHeight;
		this.originalCx = this.originalCanvasEl.getContext('2d');
		this.originalCx.strokeStyle = '#000';

		const maxWidth = screen.width;
		const maxHeight = screen.height;

		const scale = Math.min((maxWidth / this.imageWidth), (maxHeight / this.imageHeight));
		const iwScaled = this.imageWidth * scale;
		const ihScaled = this.imageHeight * scale;

		this.canvasEl.width = iwScaled;
		this.canvasEl.height = ihScaled;

		this.captureEvents(this.canvasEl);

		let that = this;
		
		this.img.onload = function () {

			that.resizeCanvas();
			
			// Setup original canvas
			that.originalCx.lineCap = 'round';
			that.originalCx.strokeStyle = that.strokeColor;
			that.originalCx.drawImage(that.img, 0, 0, that.imageWidth, that.imageHeight);
		};

	}

	private resizeCanvas() {

		const maxWidth = this.window.innerWidth;
		const maxHeight = this.window.innerHeight;

		const scale = Math.min((maxWidth / this.imageWidth), (maxHeight / this.imageHeight));
		const iwScaled = this.imageWidth * scale;
		const ihScaled = this.imageHeight * scale;

		this.canvasEl.width = iwScaled;
		this.canvasEl.height = ihScaled;

		// Setup display/drawing canvas
		this.cx.lineWidth = this.lineWidth;
		this.cx.lineCap = 'round';
		this.cx.strokeStyle = this.strokeColor;
		this.cx.drawImage(this.img, 0, 0, iwScaled, ihScaled);
	}

	private captureEvents(canvasEl: HTMLCanvasElement) {

		this.captureTouchEvents(canvasEl);
		this.captureMouseEvents(canvasEl);
	}

	private captureTouchEvents(canvasEl: HTMLCanvasElement) {
		fromEvent(canvasEl, 'touchstart')
			.pipe(
				switchMap((e) => {
					// after a mouse down, we'll record all mouse moves
					return fromEvent(canvasEl, 'touchmove')
						.pipe(
							// we'll stop (and unsubscribe) once the user releases the mouse
							// this will trigger a 'mouseup' event    
							takeUntil(fromEvent(canvasEl, 'touchend')),
							// pairwise lets us get the previous value to draw a line from
							// the previous point to the current point    
							pairwise()
						);
				})
			)
			.subscribe((res: [TouchEvent, TouchEvent]) => {
				const rect = canvasEl.getBoundingClientRect();

				// previous and current position with the offset
				const prevPos = {
					x: res[0].touches[0].clientX - rect.left,
					y: res[0].touches[0].clientY - rect.top
				};

				const currentPos = {
					x: res[1].touches[0].clientX - rect.left,
					y: res[1].touches[0].clientY - rect.top
				};

				// this method we'll implement soon to do the actual drawing
				this.drawOnCanvas(prevPos, currentPos);
				this.drawOnOrignalCanvas(prevPos, currentPos);
			});
	}

	private captureMouseEvents(canvasEl: HTMLCanvasElement) {

		fromEvent(canvasEl, 'mousedown')
			.pipe(
				switchMap((e) => {
					// after a mouse down, we'll record all mouse moves
					return fromEvent(canvasEl, 'mousemove')
						.pipe(
							// we'll stop (and unsubscribe) once the user releases the mouse
							// this will trigger a 'mouseup' event    
							takeUntil(fromEvent(canvasEl, 'mouseup')),
							// we'll also stop (and unsubscribe) once the mouse leaves the canvas (mouseleave event)
							takeUntil(fromEvent(canvasEl, 'mouseleave')),
							// pairwise lets us get the previous value to draw a line from
							// the previous point to the current point    
							pairwise()
						)
				})
			)
			.subscribe((res: [MouseEvent, MouseEvent]) => {
				const rect = canvasEl.getBoundingClientRect();

				// previous and current position with the offset
				const prevPos = {
					x: res[0].clientX - rect.left,
					y: res[0].clientY - rect.top
				};

				const currentPos = {
					x: res[1].clientX - rect.left,
					y: res[1].clientY - rect.top
				};

				this.drawOnCanvas(prevPos, currentPos);
				this.drawOnOrignalCanvas(prevPos, currentPos);
			});
	}

	private drawOnCanvas(prevPos: { x: number, y: number }, currentPos: { x: number, y: number }) {

		if (!this.cx) { return; }

		this.cx.beginPath();

		if (prevPos) {
			this.cx.moveTo(prevPos.x, prevPos.y);
			this.cx.lineTo(currentPos.x, currentPos.y);
			this.cx.stroke();
		}
	}

	private drawOnOrignalCanvas(prevPos: { x: number, y: number }, currentPos: { x: number, y: number }) {

		if (!this.originalCx) { return; }

		const xScale = (this.imageWidth / this.canvasEl.width);
		const yScale = (this.imageHeight / this.canvasEl.height);

		let prevX = xScale * prevPos.x;
		let prevY = yScale * prevPos.y;

		let curX = xScale * currentPos.x;
		let curY = yScale * currentPos.y;

		// Scale linewidth with original canvas
		this.originalCx.lineWidth = ((yScale + xScale) / 2) * this.lineWidth;
		this.originalCx.beginPath();

		if (prevPos) {
			this.originalCx.moveTo(prevX, prevY);
			this.originalCx.lineTo(curX, curY);
			this.originalCx.stroke();
		}
	}

	selectColor(color: string) {

		this.imgOptions = false;

		this.strokeColor = color;
		this.cx.strokeStyle = color;
		this.originalCx.strokeStyle = color;
	}

	cancelEdit() {

		this.closeOverlay.emit();
	}

	saveImage() {

		// If size is bigger than 5mb -> show saving img loader
		var base64str = this.originalImg.src.split('base64,')[1];
		var size = atob(base64str).length;

		if (size > 5000000) {

			this.showSavingLoader = true;
		}

		// Set timeout so threads are not locked
		setTimeout(() => {
			
			// Get the original canvas for saving (to retain quality)
			var dataUrl = this.originalCanvas.nativeElement.toDataURL();
			this.closeOverlay.emit(dataUrl);
		}, 0);
	}

	closeOptions(e: Event) {

		const target = e.target as HTMLElement;

		if (!target.classList.contains('showImageOptions'))
			this.imgOptions = false;
	}

	ngOnDestroy(): void {

		// Remove temp classes for drawing on canvas
		this.renderer.removeClass(this.document.getElementById('app-root'), 'd-none');
		this.renderer.removeClass(this.document.body, 'no-refresh');
	}
}